import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoBar from './info-bar';
import BordersAndSettingIcon from '../borders-and-setting-icon';
import RestTimerCircle from '../reset-between-sets/rest-timer-circle';
import Skeleton from '../../skeleton';
import ExercisePlaceholderImageEn from '../../../assets/svgs/exercise-placeholder.svg';
import ExercisePlaceholderImageAr from '../../../assets/svgs/exercise-placeholder-ar.svg';
import { DEFAULT_INTRO_INTERVAL_SECONDS, screenSizes } from '../../../constants/constants';
import { useWindowDimensions, usePrefetchedMediaSrc } from '../../../hooks';
import tw from 'twin.macro';
import { css } from 'styled-components';

// On iOS Safari the static viewport (100vh / inset-0) is taller than the
// visible area, so the intro shell ran underneath the browser's bottom toolbar
// and clipped the info bar's labels. dvh tracks the *visible* height; the vh
// line above it is the fallback for engines without dvh, and must stay a
// separate declaration — a `tw` class pair would collapse to one height key.
const shellHeight = css`
  height: 100vh;
  height: 100dvh;
`;

// Landscape phone (Figma 2057-14797): a white surface with one rounded, clipped
// card holding both halves — exercise image left, info column right, no gap
// between them, so only the outer corners are round. The horizontal padding is
// tied to the safe area: on a notched iPhone in landscape the notch takes that
// inset, which is the gutter the design draws.
const landscapeSurface = css`
  background: #ffffff;
  padding: 8px max(10px, env(safe-area-inset-right)) 8px max(10px, env(safe-area-inset-left));
`;

const landscapeCard = tw`flex min-h-0 flex-1 overflow-hidden rounded-[12px]`;

// The image sits on a light card rather than the portrait screen's black + blurred
// backdrop: the design shows the subject on the studio's own light background, and
// a blurred fill behind a light photo reads as a smudge rather than a letterbox.
const landscapeImageCell = tw`relative flex-1 overflow-hidden bg-[#F1F1F1]`;

// One intro for both execution modes. Nothing on this screen is camera-driven —
// the image, the countdown ring and the InfoBar all come off the intro payload,
// and the countdown starts on the image's own onLoad — so an offline exercise
// gets the same preview (name, reps/duration, sets) as a motion-captured one
// instead of the reduced no-camera variant it used to fall back to.
const ExerciseIntro = ({
  countdown,
  dispatch,
  timer,
  sendJsonMessage,
  onVideoReady,
  introResult,
}) => {
  const { t, i18n } = useTranslation('exercise');
  const ExercisePlaceholderImage = i18n.language?.startsWith('ar')
    ? ExercisePlaceholderImageAr
    : ExercisePlaceholderImageEn;
  const [imageLoaded, setImageLoaded] = useState(false);
  const { clientWindowWidth, clientWindowHeight } = useWindowDimensions();
  // Landscape phone gets the card layout below. Read in JS, not a media query,
  // because the controls move to a different parent (the info column's top row)
  // rather than just changing style.
  const isLandscapeMobile =
    clientWindowWidth < screenSizes.lg && clientWindowWidth > clientWindowHeight;

  // introResult stays falsy until the socket sends the exercise payload —
  // show a skeleton in place of the image/details until then, and keep it
  // up while the image itself is still fetching.
  const isDataLoading = !introResult;
  const showImageSkeleton = isDataLoading || !imageLoaded;

  // The intro countdown only starts once onVideoReady fires, and that used to
  // hang off this image's onLoad — so an exercise the backend sends without an
  // imageUrl (or with a broken one) left `src` empty, onLoad never fired, and
  // the countdown sat at zero forever. Fall back to a bundled placeholder so
  // there is always an image that resolves, and treat a load error as ready
  // too (see handleImageError) so a dead URL can't stall the intro either.
  //
  // usePrefetchedMediaSrc resolves to a warmed blob: URL when camera-permission
  // already prefetched this session's media and it hasn't expired — otherwise
  // it resolves to introResult?.imageUrl unchanged, so the fallback below still
  // applies exactly as before.
  const resolvedImageSrc = usePrefetchedMediaSrc(introResult?.imageUrl) || ExercisePlaceholderImage;
  const resolvedExerciseName = introResult?.exerciseName;
  const resolvedReps = introResult?.reps;
  const resolvedSets = introResult?.sets;
  // Only REP_BASED ends on the rep count, so only it shows reps here; every other
  // type is measured by the clock and shows the set budget (`set_eta_seconds`).
  // InfoBar decides which to render from `type`.
  const resolvedType = introResult?.type;
  const resolvedDuration = introResult?.set_eta_seconds;
  // Ring scale = the backend's intro interval, the same value the countdown
  // hook is seeded with, so ring and number always agree.
  const backendIntroInterval = Number(introResult?.introInterval);
  const resolvedIntroInterval =
    Number.isFinite(backendIntroInterval) && backendIntroInterval > 0
      ? backendIntroInterval
      : DEFAULT_INTRO_INTERVAL_SECONDS;

  const handleImageLoad = () => {
    setImageLoaded(true);
    onVideoReady?.();
  };

  // A broken/expired image URL must not block the countdown — swap in the
  // placeholder and report ready, same as a successful load.
  const handleImageError = event => {
    if (event.currentTarget.src !== ExercisePlaceholderImage) {
      event.currentTarget.src = ExercisePlaceholderImage;
      return;
    }
    handleImageLoad();
  };

  const controls = (
    <>
      {isDataLoading ? (
        <Skeleton skeletonStyle={tw`w-[60px] h-[60px] rounded-full`} />
      ) : (
        <RestTimerCircle timer={countdown} convertOn={resolvedIntroInterval} />
      )}
      <div
        tw="flex items-center gap-2 rounded-[12px] bg-black/70 p-2 text-white shadow-[0_12px_30px_rgba(0,0,0,0.24)]"
        // On the white control row the drop shadow has nothing to sit against and
        // the button reads as the design's solid dark square instead.
        css={isLandscapeMobile && tw`rounded-[10px] bg-[#3A3A3A] px-3 py-2 shadow-none`}
      >
        <BordersAndSettingIcon
          dispatch={dispatch}
          timer={timer}
          sendJsonMessage={sendJsonMessage}
        />
      </div>
    </>
  );

  const exerciseImage = (
    <>
      {showImageSkeleton && <Skeleton skeletonStyle={tw`absolute inset-0`} />}
      {!isDataLoading && (
        <>
          {/* Blurred background — fills letterbox/pillarbox areas. Portrait and
              desktop only: landscape puts the image on the design's light card,
              where a blurred copy behind it reads as a smudge. */}
          {!isLandscapeMobile && (
            <img
              loading="lazy"
              decoding="async"
              src={resolvedImageSrc}
              alt=""
              aria-hidden="true"
              tw="absolute inset-0 w-full h-full object-cover scale-110"
              style={{ filter: 'blur(24px) brightness(0.45) saturate(1.4)' }}
            />
          )}
          {/* Main image — signals ready on load */}
          <img
            loading="lazy"
            decoding="async"
            src={resolvedImageSrc}
            alt={resolvedExerciseName ?? t('exercisePreview.altFallback')}
            tw="relative w-full h-full object-contain"
            data-testid="exercise-intro-image"
            style={{ opacity: imageLoaded ? 1 : 0 }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        </>
      )}
    </>
  );

  const infoBar = (
    <InfoBar
      exerciseName={resolvedExerciseName}
      reps={resolvedReps}
      sets={resolvedSets}
      duration={resolvedDuration}
      exerciseType={resolvedType}
      isLoading={isDataLoading}
      controls={isLandscapeMobile ? controls : null}
    />
  );

  if (isLandscapeMobile) {
    return (
      <div css={[shellHeight, landscapeSurface]} tw="fixed inset-x-0 top-0 z-[800] flex flex-col">
        <div css={landscapeCard}>
          <div css={landscapeImageCell}>{exerciseImage}</div>
          {infoBar}
        </div>
      </div>
    );
  }

  return (
    <div css={shellHeight} tw="fixed inset-x-0 top-0 z-[800] flex flex-col">
      <div tw="flex-1 relative overflow-hidden bg-black">
        {exerciseImage}
        <div tw="absolute top-4 right-4 flex items-center gap-2 z-[500]">{controls}</div>
      </div>
      {infoBar}
    </div>
  );
};

export default ExerciseIntro;
